import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import TERMS_CONTENT, { TERMS_VERSION } from "../data/termsOfUse";
import Seo from "../components/Seo";

const normalizeLangKey = (lang) => {
  if (!lang) return "en";
  const l = lang.toLowerCase();
  if (l.startsWith("pt")) return "pt";
  if (l.startsWith("es")) return "es";
  return "en";
};

const Subsection = ({ sub }) => (
  <div className="mt-4 pl-4 border-l-2 border-gray-200">
    <h3 className="text-base font-semibold text-gray-800 mb-2">{sub.title}</h3>
    {sub.paragraphs?.map((p, i) => (
      <p key={`p-${i}`} className="text-sm text-gray-700 leading-relaxed mb-2">
        {p}
      </p>
    ))}
    {sub.list && (
      <ul className="list-disc pl-5 text-sm text-gray-700 leading-relaxed mb-2 space-y-1">
        {sub.list.map((item, i) => (
          <li key={`l-${i}`}>{item}</li>
        ))}
      </ul>
    )}
    {sub.paragraphs2?.map((p, i) => (
      <p key={`p2-${i}`} className="text-sm text-gray-700 leading-relaxed mb-2">
        {p}
      </p>
    ))}
    {sub.list2 && (
      <ul className="list-disc pl-5 text-sm text-gray-700 leading-relaxed mb-2 space-y-1">
        {sub.list2.map((item, i) => (
          <li key={`l2-${i}`}>{item}</li>
        ))}
      </ul>
    )}
    {sub.paragraphs3?.map((p, i) => (
      <p key={`p3-${i}`} className="text-sm text-gray-700 leading-relaxed mb-2">
        {p}
      </p>
    ))}
  </div>
);

const Section = ({ section }) => (
  <div className="mb-8">
    <h2 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">
      {section.title}
    </h2>
    {section.paragraphs?.map((p, i) => (
      <p key={`p-${i}`} className="text-sm text-gray-700 leading-relaxed mb-3">
        {p}
      </p>
    ))}
    {section.list && (
      <ul className="list-disc pl-5 text-sm text-gray-700 leading-relaxed mb-3 space-y-1">
        {section.list.map((item, i) => (
          <li key={`l-${i}`}>{item}</li>
        ))}
      </ul>
    )}
    {section.paragraphs2?.map((p, i) => (
      <p key={`p2-${i}`} className="text-sm text-gray-700 leading-relaxed mb-3">
        {p}
      </p>
    ))}
    {section.list2 && (
      <ul className="list-disc pl-5 text-sm text-gray-700 leading-relaxed mb-3 space-y-1">
        {section.list2.map((item, i) => (
          <li key={`l2-${i}`}>{item}</li>
        ))}
      </ul>
    )}
    {section.paragraphs3?.map((p, i) => (
      <p key={`p3-${i}`} className="text-sm text-gray-700 leading-relaxed mb-3">
        {p}
      </p>
    ))}
    {section.subsections?.map((sub, i) => (
      <Subsection key={`s-${i}`} sub={sub} />
    ))}
  </div>
);

const TermsOfUse = () => {
  const { i18n } = useTranslation();
  const [selectedLang, setSelectedLang] = React.useState(normalizeLangKey(i18n.language));

  React.useEffect(() => {
    setSelectedLang(normalizeLangKey(i18n.language));
  }, [i18n.language]);

  const content = TERMS_CONTENT[selectedLang] || TERMS_CONTENT.en;

  const labels = {
    pt: { back: "Voltar", version: "Versão" },
    en: { back: "Back", version: "Version" },
    es: { back: "Volver", version: "Versión" },
  };
  const L = labels[selectedLang] || labels.en;

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-100 py-6 px-4">
      <Seo
        title="Termos de Uso"
        description="Termos de uso da plataforma Alpha Translations."
        path="/terms-of-use"
      />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-3">
          <Link
            to="/"
            className="text-gray-500 hover:text-blue-600 text-xs font-medium flex items-center gap-1 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {L.back}
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-sm font-semibold text-gray-900">Alpha Translations</h1>
          </div>
          <div className="flex gap-1">
            {["pt", "en", "es"].map((lng) => (
              <button
                key={lng}
                onClick={() => setSelectedLang(lng)}
                className={`px-2 py-1 text-[10px] font-semibold uppercase rounded transition-colors ${
                  selectedLang === lng
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {lng}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-10">
          <div className="mb-8 pb-6 border-b border-gray-100">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{content.title}</h1>
            <p className="text-xs text-gray-500">
              {content.lastUpdated} · {L.version}: {TERMS_VERSION}
            </p>
          </div>

          <p className="text-sm text-gray-700 leading-relaxed mb-8 italic">{content.intro}</p>

          {content.sections.map((section, i) => (
            <Section key={i} section={section} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
